import { useState, useCallback } from 'react';
import { apiService } from '../services/api';
import type { StaffAppointmentListParams, StaffAppointmentListResponse, StaffAppointmentDetailDto } from '../types/api-responses';
import { AppointmentStatus } from '../types/enums';
import { getApiErrorMessage } from '../types/api-errors';

export const useAppointments = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const getAppointments = useCallback(async (params?: StaffAppointmentListParams): Promise<StaffAppointmentListResponse> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getStaffAppointments(params);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to fetch appointments');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const getAppointmentById = useCallback(async (id: number): Promise<StaffAppointmentDetailDto> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getStaffAppointmentById(id);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to fetch appointment details');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const updateStatus = useCallback(async (id: number, status: AppointmentStatus): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.updateAppointmentStatus(id, status);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to update appointment status');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const confirmAppointment = useCallback(async (id: number): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.confirmAppointment(id);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to confirm appointment');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const cancelAppointment = useCallback(async (id: number, reason?: string): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.cancelAppointment(id, reason);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to cancel appointment');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const rescheduleAppointment = useCallback(async (id: number, newDate: string): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.rescheduleAppointment(id, newDate);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to reschedule appointment');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteAppointment = useCallback(async (id: number): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.deleteAppointment(id);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to delete appointment');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        getAppointments,
        getAppointmentById,
        updateStatus,
        confirmAppointment,
        cancelAppointment,
        rescheduleAppointment,
        deleteAppointment,
        loading,
        error
    };
};
